import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AlertCircle, ArrowLeft, CheckCircle2, KeyRound, LoaderCircle } from "lucide-react";
import { useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FieldError } from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { useAceptarInvitacion, useValidarInvitacion } from "@/lib/auth";
import { evaluarPassword, passwordValida, REQUISITOS_PASSWORD } from "@/lib/password";
import { AuthShell } from "./AuthShell";
import { PasswordChecklist } from "./PasswordChecklist";
import { PasswordInput } from "./PasswordInput";

// Validación con reglas nativas de react-hook-form apoyadas en lib/password (mismo motivo
// que ResetPasswordPage: el zodResolver del repo no soporta zod v4).
type InvitacionFormValues = {
  password: string;
  password_confirma: string;
};

// Retardo del redirect al login tras el éxito: da tiempo a leer la confirmación.
const REDIRECT_DELAY_MS = 1600;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<InvitacionFormValues>,
  setFormError: (msg: string) => void,
  setResaltar: (v: boolean) => void,
): void {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "PASSWORD_INVALIDA":
        setResaltar(true);
        setError("password", { message: err.message });
        return;
      case "TOKEN_INVALIDO":
        setFormError(
          "El enlace de invitación no es válido o ha caducado. Pide una nueva invitación al administrador.",
        );
        return;
      case "CAMPOS_REQUERIDOS":
        setFormError("Rellena todos los campos.");
        return;
      default:
        setFormError(err.message || "No se pudo aceptar la invitación.");
        return;
    }
  }
  setFormError("No se pudo aceptar la invitación. Comprueba tu conexión.");
}

// EnlaceNoValido es la tarjeta que se muestra sin token o con un enlace muerto (usado,
// revocado o caducado). No distingue el motivo: solo el administrador puede resolverlo.
function EnlaceNoValido() {
  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Enlace no válido o caducado</CardTitle>
          <CardDescription>Este enlace de invitación ya no sirve.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>Pide una nueva invitación al administrador que te dio de alta.</span>
          </Alert>
          <p className="mt-2 text-center text-sm">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Volver a iniciar sesión
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

/**
 * InvitacionPage es la página PÚBLICA donde se canjea la invitación de registro
 * (`/invitacion?token=…`, #239 D13), hermana de `/restablecer`. Al cargar valida el enlace
 * contra el servidor; si sigue vivo saluda a su destinatario, le recuerda con qué usuario
 * entrará y le pide su primera contraseña. Al aceptar no abre sesión: lleva al login.
 */
export function InvitacionPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: "/invitacion" });
  const invitacion = useValidarInvitacion(token);
  const aceptar = useAceptarInvitacion();
  const [formError, setFormError] = useState<string | null>(null);
  const [resaltar, setResaltar] = useState(false);
  const [exito, setExito] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<InvitacionFormValues>({
    mode: "onTouched",
    defaultValues: { password: "", password_confirma: "" },
  });

  const password = watch("password");
  const passwordConfirma = watch("password_confirma");
  const checks = evaluarPassword(password);
  const coincide = passwordConfirma.length > 0 && password === passwordConfirma;

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    setResaltar(false);
    try {
      await aceptar.mutateAsync({ token: token ?? "", password: values.password });
      setExito(true);
      setTimeout(() => {
        void navigate({ to: "/login" });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      aplicarError(err, setError, setFormError, setResaltar);
    }
  });

  // Sin token no hay nada que canjear y no se consulta al servidor; un enlace muerto se
  // trata igual (el backend no distingue el motivo: anti-enumeración).
  if (!token || invitacion.isError) {
    return <EnlaceNoValido />;
  }

  if (invitacion.isPending) {
    return (
      <AuthShell>
        <Card>
          <CardContent>
            <div className="flex items-center justify-center gap-2 py-10 text-slate-500">
              <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
              Comprobando la invitación…
            </div>
          </CardContent>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Card>
        <CardHeader>
          <CardTitle>Hola {invitacion.data.nombre}</CardTitle>
          <CardDescription>
            Te han invitado a LOTO System. Elige tu contraseña para entrar; tu usuario es{" "}
            <strong className="text-slate-900">{invitacion.data.nombre_usuario}</strong>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {exito ? (
            <Alert variant="success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <span>
                <strong>Ya puedes iniciar sesión.</strong> Te llevamos a la pantalla de acceso.
              </span>
            </Alert>
          ) : (
            <>
              {formError ? (
                <Alert variant="destructive">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                  <span>{formError}</span>
                </Alert>
              ) : null}

              <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Contraseña</Label>
                  <PasswordInput
                    id="password"
                    autoComplete="new-password"
                    aria-invalid={errors.password ? true : undefined}
                    {...register("password", {
                      validate: (v) => passwordValida(v) || REQUISITOS_PASSWORD,
                      onChange: () => {
                        if (getValues("password_confirma")) void trigger("password_confirma");
                      },
                    })}
                  />
                  <FieldError>{errors.password?.message}</FieldError>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password_confirma">Repetir contraseña</Label>
                  <PasswordInput
                    id="password_confirma"
                    autoComplete="new-password"
                    aria-invalid={errors.password_confirma ? true : undefined}
                    {...register("password_confirma", {
                      validate: (v) =>
                        v === getValues("password") || "Las contraseñas no coinciden",
                    })}
                  />
                  <FieldError>{errors.password_confirma?.message}</FieldError>
                </div>

                <PasswordChecklist checks={checks} coincide={coincide} resaltar={resaltar} />

                <Button
                  type="submit"
                  size="lg"
                  disabled={aceptar.isPending}
                  className="mt-2 w-full"
                >
                  {aceptar.isPending ? (
                    <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
                  ) : (
                    <KeyRound className="h-5 w-5" aria-hidden="true" />
                  )}
                  Crear mi contraseña
                </Button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </AuthShell>
  );
}
